import {useTranslation} from 'react-i18next';
import {
	useAppendVersionMetadataMutation,
	useReplaceVersionMetadataMutation,
	GetCompareDocumentDataQuery,
} from '../../../hooks';
import {useUserContext} from 'authentication/UserContext';
import {IComparedItem} from '../compareDocuments/compareDocuments';
import {
	KeywordAssignment,
	RegulatoryDocument,
	RegulatoryDocumentParagraph,
	VexClusterAssignment,
} from 'types';
import {GetRegulatoryDocumentDetailsDocument} from 'features/RegulatoryDocuments/hooks/useGetRegulatoryDocumentDetails.generated';
import {useAssignRegulatoryDocumentParagraphsRequirementsDocumentCompareMutation} from 'features/RegulatoryDocuments/hooks/useAssignRegulatoryDocumentParagraphsRequirementsDocumentCompare.generated';
import _ from 'lodash';
import {useCloneRequirement} from 'features/Requirements/hooks/useCloneRequirement';
import {
	prepareRequirementsIdsForUpdate,
	createCloneRequirements,
} from './CompareVersionPanel.utils';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {
	ComparisonUpdateFields,
	ComparisonUpdateFieldType,
} from './compareVersionTypes';

export const useComparisonUpdate = (
	selectedItems: IComparedItem[],
	sourceVersion: NonNullable<GetCompareDocumentDataQuery['regulatoryDocument']>,
	newVersion: NonNullable<GetCompareDocumentDataQuery['regulatoryDocument']>,
	data: any,
) => {
	const {t} = useTranslation('features/regulatorydocuments', {
		keyPrefix: 'CompareVersionPanel',
	});
	const {isVex} = useUserContext();

	const {createCloneForMerging, getInitialVersion} = useCloneRequirement();
	const [appendVersionMetadata] = useAppendVersionMetadataMutation();
	const [replaceVersionMetadata] = useReplaceVersionMetadataMutation();
	const [updateParagraphsRequirementsCompareDocument] =
		useAssignRegulatoryDocumentParagraphsRequirementsDocumentCompareMutation();

	const [allFields, setAllFields] = useState<ComparisonUpdateFields>([]);
	const [selectedFields, setSelectedFields] = useState<ComparisonUpdateFields>(
		[],
	);

	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		let allFields: ComparisonUpdateFields = [];
		if (isVex) {
			allFields = ['tags', 'requirements', 'keywordAssignments'];
		} else {
			allFields = [
				'vehicleCategories',
				'keywords',
				'categories',
				'driveVariants',

				'dateNewTypes',
				'dateNewRegistration',
				'comprehensive',
				'modelYear',

				'phaseIn',
				'phaseOut',
				'attachments',

				'summary',
				'tags',
			];
		}
		setAllFields(allFields);
		setSelectedFields(allFields);
	}, [isVex]);

	const concatAndRemoveDuplicates = (arr1: any[], arr2: any[]): any[] => {
		return arr1.concat(arr2).reduce((acc, curr) => {
			if (!acc.some((el: {id: string}) => el.id === curr.id)) acc.push(curr);
			return acc;
		}, [] as any[]);
	};

	// Merges two lists of KeywordAssignments
	function mergeKeywordAssignments(
		oldValue: KeywordAssignment[],
		newValue: KeywordAssignment[],
	): KeywordAssignment[] {
		const result = new Map<string, KeywordAssignment>();

		const combinedList = [...oldValue, ...newValue];

		for (const item of combinedList) {
			const keywordId = item.keyword.id;

			if (result.has(keywordId)) {
				const existing = result.get(keywordId)!;
				existing.vexClusterAssignments = mergeVexClusterAssignments(
					item.vexClusterAssignments,
					existing.vexClusterAssignments,
				);
				continue;
			}

			result.set(keywordId, {
				...item,
				vexClusterAssignments: [...item.vexClusterAssignments],
			});
		}

		return Array.from(result.values());
	}

	function mergeVexClusterAssignments(
		oldValue: VexClusterAssignment[],
		newValue: VexClusterAssignment[],
	): VexClusterAssignment[] {
		const seen = new Set<string>();
		const merged: VexClusterAssignment[] = [];

		for (const item of oldValue) {
			merged.push(item);
			seen.add(item.vexCluster.id);
		}

		for (const item of newValue) {
			if (!seen.has(item.vexCluster.id)) {
				merged.push(item);
				seen.add(item.vexCluster.id);
			}
		}

		return merged;
	}

	const concatField = (
		oldParagraph: RegulatoryDocumentParagraph,
		newParagraph: RegulatoryDocumentParagraph, // <-- this should be a mutable copy!
		fieldName: ComparisonUpdateFieldType,
		replaceDistData: boolean,
		resultDocParagraph?: RegulatoryDocumentParagraph,
	) => {
		if (!selectedFields.includes(fieldName)) return;

		if (replaceDistData) {
			newParagraph[fieldName] = oldParagraph[fieldName];
		} else if (Array.isArray(newParagraph[fieldName])) {
			newParagraph[fieldName] = concatAndRemoveDuplicates(
				oldParagraph[fieldName] || [],
				newParagraph[fieldName] || [],
			);
		} else if (oldParagraph[fieldName]) {
			newParagraph[fieldName] = oldParagraph[fieldName];
		} else {
			// Some fields are not supported yet
		}

		if (fieldName === 'keywords' || fieldName === 'keywordAssignments') {
			if (isVex) {
				newParagraph.keywordAssignments =
					resultDocParagraph?.keywordAssignments;
			} else if (replaceDistData) {
				newParagraph.keywordAssignments = oldParagraph.keywordAssignments;
			} else {
				newParagraph.keywordAssignments = mergeKeywordAssignments(
					oldParagraph.keywordAssignments ?? [],
					newParagraph.keywordAssignments ?? [],
				);
			}
		}
	};

	const getUpdatedComparedItems = (
		newComparisonItems: IComparedItem[],
		replaceDistData: boolean,
		resultDoc: RegulatoryDocument,
	) => {
		selectedItems.forEach(selectedItem => {
			const paragraph =
				selectedItem.newParagraph as unknown as RegulatoryDocumentParagraph;
			const {id} = paragraph;
			const newComparisonIndex = newComparisonItems.findIndex(
				ci => (ci.newParagraph as any).id === id,
			);
			// create a copy to avoid updating the original collection
			const newComparisonParagraph = {
				...(newComparisonItems[newComparisonIndex].newParagraph as any),
			} as RegulatoryDocumentParagraph;
			const oldComparisonParagraph = newComparisonItems[newComparisonIndex]
				.oldParagraph as any as RegulatoryDocumentParagraph;

			const resultDocParagraph = resultDoc.paragraphs.find(p => p.id === id);

			allFields.forEach(field =>
				concatField(
					oldComparisonParagraph,
					newComparisonParagraph,
					field,
					replaceDistData,
					resultDocParagraph,
				),
			);
			newComparisonItems[newComparisonIndex].newParagraph =
				newComparisonParagraph;
		});
		return newComparisonItems;
	};

	const getInput = () => {
		return {
			sourceRegulatoryDocumentId: sourceVersion.id,
			distRegulatoryDocumentId: newVersion.id,
			sourceParagraphIds: selectedItems.map(
				item =>
					(item.oldParagraph as unknown as RegulatoryDocumentParagraph).id,
			),
			distParagraphIds: selectedItems.map(
				item =>
					(item.newParagraph as unknown as RegulatoryDocumentParagraph).id,
			),
			updateFields: {
				copyVehicleCategories:
					selectedFields.indexOf('vehicleCategories') !== -1,
				copyKeywords: selectedFields.indexOf('keywords') !== -1,
				copyCategories: selectedFields.indexOf('categories') !== -1,
				copyDriveVariants: selectedFields.indexOf('driveVariants') !== -1,

				copyNewTypes: selectedFields.indexOf('dateNewTypes') !== -1,
				copyNewRegistration:
					selectedFields.indexOf('dateNewRegistration') !== -1,
				copyComprehensive: selectedFields.indexOf('comprehensive') !== -1,
				copyModelYear: selectedFields.indexOf('modelYear') !== -1,

				copyPhaseIn: selectedFields.indexOf('phaseIn') !== -1,
				copyPhaseOut: selectedFields.indexOf('phaseOut') !== -1,
				copyAttachments: selectedFields.indexOf('attachments') !== -1,

				copySummary: selectedFields.indexOf('summary') !== -1,
				copyTags: selectedFields.indexOf('tags') !== -1,
				copyRequirements: selectedFields.indexOf('requirements') !== -1,

				copyKeywordAssignments:
					selectedFields.indexOf('keywordAssignments') !== -1,
			},
		};
	};

	const updateRequirements = async (): Promise<boolean> => {
		if (selectedFields.indexOf('requirements') === -1) {
			return false;
		}

		const map = await createCloneRequirements(
			data,
			selectedItems,
			createCloneForMerging,
			getInitialVersion,
		);

		await updateParagraphsRequirementsCompareDocument({
			variables: {
				input: {
					regulatoryDocumentId: newVersion.id,
					requirementRefs: selectedItems.map(item =>
						prepareRequirementsIdsForUpdate(item, map),
					),
					paragraphIds: selectedItems.map(item => {
						const {newParagraph} = item;
						return (newParagraph as any).id;
					}),
				},
			},
			refetchQueries: [GetRegulatoryDocumentDetailsDocument],
		});
		return true;
	};

	const appendMetadata = async (
		newComparisonItems: IComparedItem[],
	): Promise<IComparedItem[]> => {
		setIsLoading(true);
		const transferResult = await appendVersionMetadata({
			variables: {
				input: getInput(),
			},
		});

		const resultDoc = transferResult?.data?.appendVersionMetadata
			?.regulatoryDocument as RegulatoryDocument;

		const ret = getUpdatedComparedItems(newComparisonItems, false, resultDoc);
		await updateRequirements();
		setIsLoading(false);
		return ret;
	};

	const replaceMetadata = async (
		newComparisonItems: IComparedItem[],
	): Promise<IComparedItem[]> => {
		setIsLoading(true);
		const transferResult = await replaceVersionMetadata({
			variables: {
				input: getInput(),
			},
		});

		const resultDoc = transferResult?.data?.replaceVersionMetadata
			?.regulatoryDocument as RegulatoryDocument;

		const ret = getUpdatedComparedItems(newComparisonItems, true, resultDoc);
		await updateRequirements();
		setIsLoading(false);
		return ret;
	};

	const checkboxData = useMemo(
		() =>
			allFields.map(sf => {
				return {
					key: sf,
					value: t(sf[0].toUpperCase() + sf.substring(1)),
					isSelected: selectedFields.indexOf(sf) !== -1,
				};
			}),
		[allFields, selectedFields, t],
	);

	const selectField = useCallback(
		(field: ComparisonUpdateFieldType) => {
			if (selectedFields.indexOf(field) === -1) {
				setSelectedFields([...selectedFields, field]);
			}
		},
		[selectedFields],
	);

	const unselectField = useCallback(
		(field: ComparisonUpdateFieldType) => {
			const index = selectedFields.indexOf(field);
			if (index !== -1) {
				const newFields = [...selectedFields];
				newFields.splice(index, 1);
				setSelectedFields(newFields);
			}
		},
		[selectedFields],
	);

	const selectAll = useCallback(() => {
		setSelectedFields([...allFields]);
	}, [selectedFields]);

	const deselectAll = useCallback(() => {
		setSelectedFields([]);
	}, []);

	return {
		appendMetadata,
		replaceMetadata,
		checkboxData,
		selectField,
		unselectField,
		selectAll,
		deselectAll,
		selectedFields,
		isLoading,
	};
};
